import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatusPill } from '@/components/panels/SectionCard';
import { getErrorMessage } from '@/lib/errors';
import { supportApi, supportKeys, statusTone } from '@/lib/support-api';
import { ReplyBox, TicketThread } from '@/modules/support/TicketThread';

/** One of the customer's own tickets, with its thread. */
export function TicketDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const thread = useQuery({
    queryKey: supportKeys.thread(id),
    queryFn: () => supportApi.thread(id),
    enabled: id !== '',
    // Polled while the conversation is live, so a reply arrives without a manual refresh. Stopped
    // once the ticket is closed — there is nothing further to arrive, and a page that keeps
    // polling a finished conversation is a page nobody closes.
    refetchInterval: (query) =>
      query.state.data?.ticket.is_closed === true ? false : 20000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: supportKeys.thread(id) });

  const reply = useMutation({
    mutationFn: (body: string) => supportApi.reply(id, body),
    onSuccess: refresh,
  });

  const attach = useMutation({
    mutationFn: (file: File) => supportApi.attach(id, file),
    onSuccess: refresh,
  });

  const close = useMutation({
    mutationFn: () => supportApi.close(id),
    onSuccess: refresh,
  });

  if (thread.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading the conversation…
      </div>
    );
  }

  if (thread.isError) {
    return (
      <div className="space-y-4">
        <Alert variant="danger">{getErrorMessage(thread.error)}</Alert>
        <Button variant="outline" asChild>
          <Link to="/support">
            <ArrowLeft className="mr-2 size-4" />
            Back to support
          </Link>
        </Button>
      </div>
    );
  }

  if (thread.data === undefined) {
    return null;
  }

  const { ticket, messages, attachments } = thread.data;

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-2 mb-3">
          <Link to="/support">
            <ArrowLeft className="mr-2 size-4" />
            All tickets
          </Link>
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{ticket.reference}</span>
              <StatusPill label={ticket.status_label} tone={statusTone(ticket.status)} />
            </div>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">{ticket.subject}</h1>
          </div>

          {!ticket.is_closed && (
            <Button
              variant="outline"
              className="ml-auto"
              onClick={() => close.mutate()}
              disabled={close.isPending}
            >
              {close.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 size-4" />
              )}
              Close ticket
            </Button>
          )}
        </div>
      </div>

      {ticket.is_closed && (
        <Alert>
          This ticket is closed. Replying reopens it. You do not need to start a new one.
        </Alert>
      )}

      {(reply.isError || attach.isError || close.isError) && (
        <Alert variant="danger">
          {getErrorMessage(reply.error ?? attach.error ?? close.error)}
        </Alert>
      )}

      <TicketThread
        messages={messages}
        attachments={attachments}
        attachmentPath={(attachmentId) => supportApi.attachmentPath(ticket.id, attachmentId)}
      />

      <Card className="p-0">
        <ReplyBox
          onSend={async (body) => {
            await reply.mutateAsync(body);
          }}
          onAttach={async (file) => {
            await attach.mutateAsync(file);
          }}
          hint="Up to 10 MB per file."
        />
      </Card>
    </div>
  );
}
